import { useState, useMemo } from 'react';
import { T, amountStyle } from '../theme';
import { formatAmount } from '../utils/formatters';
import { ChevronDown, ChevronRight, AlertTriangle, Download, TrendingUp, ArrowLeft } from 'lucide-react';
import { CATEGORY_TAX_MAP, INCOME_CATEGORY_TAX_MAP, EXPENSE_REFUND_CLASS_KEYS, resolveClassification } from '../utils/constants';
import { categoryLabel, normalizeCategory } from '../utils/categoryHelpers';
import { exportTaxXlsx } from '../utils/taxExport';
import { FinanceViewNav, FinanceViewTopBar } from './NavShared';
const MEALS_CAT = 'Meals & Entertainment (50%)';
const NON_DEDUCTIBLE = new Set(['Non-deductible (Personal)', 'Non-deductible']);
const UNCATEGORIZED_CAT = 'Uncategorized';

const EXPENSE_CLASS_KEYS = new Set(['business_expense', 'business_expense_refund', 'unclassified_debit']);

// Resolve legacy data (personal → personal_expense, isRefund → refund classification) before bucketing.
function clsOf(e) {
  return resolveClassification(e.businessClassification, e.isRefund, e.transactionDirection);
}

// Only confirmed business income appears in the income section
function isIncomeItem(e) {
  return clsOf(e) === 'business_income';
}

function isExpenseItem(e) {
  const cls = clsOf(e);
  if (cls) return EXPENSE_CLASS_KEYS.has(cls);
  return e.transactionDirection !== 'credit';
}

function isExpenseRefund(e) {
  return EXPENSE_REFUND_CLASS_KEYS.has(clsOf(e));
}

function allYears(expenses) {
  const years = new Set();
  expenses.forEach(e => { if (e.date) years.add(e.date.slice(0, 4)); });
  years.add(String(new Date().getFullYear()));
  return [...years].sort().reverse();
}

export default function TaxSummaryView({
  expenses: currentExpenses = [],
  savedFiles = [],
  defaultCurrency,
  getTaxCategory,
  onGoToDashboard,
  onGoToFinances, onGoToFinancial, onGoToExpenses, onGoToAdd, onGoToCapture, onImportCreditCard, onImportBank,
  topBarProps,
}) {
  const currentYear = String(new Date().getFullYear());
  const [year, setYear] = useState(currentYear);
  const [expanded, setExpanded] = useState(new Set());
  const [incomeExpanded, setIncomeExpanded] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  // Merge all files (expense + bank + CC).
  // For linked pairs (pairedWithId on both sides): keep the expense-file record for metadata
  // (vendor, category, receipt) but override amounts from the bank/CC record — the cleared
  // bank/CC amount is the source of truth. Skip the bank/CC side to avoid double-counting.
  const allExpenses = useMemo(() => {
    // Pass 1: index all items by id with their file type
    const byId = {};
    savedFiles.forEach(f => {
      const rt = f.reportType || 'expense';
      (f.data || []).forEach(e => { byId[e.id] = { ...e, _sourceFile: f.name, _reportType: rt }; });
    });
    currentExpenses.forEach(e => {
      if (!byId[e.id]) byId[e.id] = { ...e, _reportType: 'expense' };
    });

    // Pass 2: identify bank/CC records that are the account-side of a linked pair —
    // the matching expense-file record will be emitted instead (with amounts from here).
    const accountSideIds = new Set(
      Object.values(byId)
        .filter(e => e.pairedWithId && e._reportType !== 'expense' && byId[e.pairedWithId])
        .map(e => e.id)
    );

    // Pass 3: emit, merging bank/CC amounts into expense-file records for linked pairs
    return Object.values(byId)
      .filter(e => !accountSideIds.has(e.id))
      .map(e => {
        if (e.pairedWithId && e._reportType === 'expense') {
          const paired = byId[e.pairedWithId];
          if (paired) {
            return {
              ...e,
              total: paired.total,
              convertedTotal: paired.convertedTotal,
              currency: paired.currency,
              convertedCurrency: paired.convertedCurrency,
              conversionRate: paired.conversionRate,
              adjustmentFactor: paired.adjustmentFactor,
              conversionDate: paired.conversionDate,
            };
          }
        }
        return e;
      });
  }, [savedFiles, currentExpenses]);

  const years = useMemo(() => allYears(allExpenses), [allExpenses]);

  const filtered = useMemo(
    () => year === 'all' ? allExpenses : allExpenses.filter(e => e.date?.startsWith(year)),
    [allExpenses, year]
  );

  // Split into income vs expense items
  const { incomeItems, expenseItems } = useMemo(() => {
    const incomeItems = [];
    const expenseItems = [];
    filtered.forEach(e => {
      if (isIncomeItem(e)) incomeItems.push(e);
      else if (isExpenseItem(e)) expenseItems.push(e);
    });
    return { incomeItems, expenseItems };
  }, [filtered]);

  const irsCat = e => {
    const key = normalizeCategory(e.category);
    return getTaxCategory
      ? getTaxCategory(key, e.taxCategory)
      : (CATEGORY_TAX_MAP[key] || UNCATEGORIZED_CAT);
  };

  // Group expense items by IRS category. Refund classifications net against the category total.
  const groups = useMemo(() => {
    const map = {};
    expenseItems.forEach(e => {
      const cat = irsCat(e);
      if (!map[cat]) map[cat] = { items: [], total: 0 };
      map[cat].items.push(e);
      const amt = parseFloat(e.convertedTotal ?? e.total) || 0;
      map[cat].total += isExpenseRefund(e) ? -amt : amt;
    });
    return Object.entries(map).sort((a, b) => b[1].total - a[1].total);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseItems, getTaxCategory]);

  // Group income items (all business_income) by category
  const incomeGroups = useMemo(() => {
    const map = {};
    incomeItems.forEach(e => {
      const cat = normalizeCategory(e.category) || 'other_income';
      if (!map[cat]) map[cat] = { items: [], total: 0 };
      map[cat].items.push(e);
      map[cat].total += parseFloat(e.convertedTotal ?? e.total) || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].total - a[1].total);
  }, [incomeItems]);

  // Summary totals
  const { gross, deductible } = useMemo(() => {
    let gross = 0, deductible = 0;
    groups.forEach(([cat, { total }]) => {
      gross += total;
      if (NON_DEDUCTIBLE.has(cat) || cat === UNCATEGORIZED_CAT) return;
      deductible += cat === MEALS_CAT ? total * 0.5 : total;
    });
    return { gross, deductible };
  }, [groups]);

  const businessIncome = useMemo(
    () => incomeGroups.reduce((sum, [, { total }]) => sum + total, 0),
    [incomeGroups]
  );

  const netProfit = businessIncome - deductible;

  const uncategorizedCount = groups.find(([c]) => c === UNCATEGORIZED_CAT)?.[1]?.items?.length ?? 0;

  // Show the cross-year refund disclosure whenever any refund-classified item is present.
  const hasRefundItems = expenseItems.some(isExpenseRefund);

  const toggle = cat => setExpanded(prev => {
    const next = new Set(prev);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    return next;
  });

  const toggleIncome = cat => setIncomeExpanded(prev => {
    const next = new Set(prev);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    return next;
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const label = year === 'all' ? 'All time' : `${year}-01-01 to ${year}-12-31`;
      await exportTaxXlsx({
        expenseItems,
        incomeItems,
        groups,
        incomeGroups,
        deductible,
        businessIncome,
        netProfit,
        reportCurrency: defaultCurrency,
        filename: `tax-summary-${year}`,
        dateRange: label,
        isCombined: savedFiles.length > 1,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh' }}>

      {/* Nav bar */}
      <div style={{ background: T.gradNavy, padding: '14px 16px 12px' }}>
        {topBarProps && <FinanceViewTopBar {...topBarProps} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <button
            onClick={onGoToDashboard}
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 13, fontWeight: 600 }}
          >
            <ArrowLeft size={14} color="#fff" /> Dashboard
          </button>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>Tax Summary</div>
        </div>
        {/* <FinanceViewNav
          current="tax"
          onGoToDashboard={onGoToDashboard}
          onGoToFinances={onGoToFinances}
          onGoToFinancial={onGoToFinancial}
          onGoToExpenses={onGoToExpenses}
          onGoToAdd={onGoToAdd}
          onGoToCapture={onGoToCapture}
        /> */}
      </div>

      {/* Content */}
      <div style={{ padding: '20px 16px', maxWidth: 700, margin: '0 auto' }}>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
          {[
            { label: 'Total Expenses', value: gross, color: '#374151', note: null },
            { label: 'Est. Deductible', value: deductible, color: '#15803d', note: 'incl. M&E at 50%' },
            { label: 'Non-deductible', value: gross - deductible, color: '#9CA3AF', note: null },
          ].map(({ label, value, color, note }) => (
            <div key={label} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color, lineHeight: 1.1, ...amountStyle() }}>
                {formatAmount(value, defaultCurrency)}
              </div>
              {note && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{note}</div>}
            </div>
          ))}
        </div>

        {/* Refund handling disclosure */}
        {hasRefundItems && (
          <div style={{
            background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10,
            padding: '10px 12px', marginBottom: 10, fontSize: 12, color: '#166534', lineHeight: 1.45,
          }}>
            <strong>Refunds are netted into the year received,</strong> not the year of the original expense.
            For prior-year refunds with material tax impact, consult a tax professional — the IRS tax-benefit rule
            may require reporting the refund as income in the year received.
          </div>
        )}

        {/* Income + Net Profit cards */}
        {incomeGroups.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div style={{ background: '#F0FDF4', borderRadius: 12, padding: '14px 16px', border: '1px solid #BBF7D0' }}>
              <div style={{ fontSize: 10, color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Biz Income</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: '#15803d', lineHeight: 1.1, ...amountStyle() }}>
                {formatAmount(businessIncome, defaultCurrency)}
              </div>
            </div>
            <div style={{
              background: netProfit >= 0 ? '#F0FDF4' : '#FEF2F2',
              borderRadius: 12, padding: '14px 16px',
              border: `1px solid ${netProfit >= 0 ? '#BBF7D0' : '#FECACA'}`,
            }}>
              <div style={{ fontSize: 10, color: netProfit >= 0 ? '#166534' : '#991B1B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Net Biz Profit</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: netProfit >= 0 ? '#15803d' : '#DC2626', lineHeight: 1.1, ...amountStyle() }}>
                {netProfit >= 0 ? '' : '−'}{formatAmount(Math.abs(netProfit), defaultCurrency)}
              </div>
              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>income − deductible</div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'black' }}>
            {filtered.length} expenses.
          </div>
          <select
            value={year}
            onChange={e => { setYear(e.target.value); setExpanded(new Set()); setIncomeExpanded(new Set()); }}
            style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: 'black', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', outline: 'none' }}
          >
            {years.map(y => <option key={y} value={y} style={{ color: '#111', background: '#fff' }}>{y}</option>)}
            <option value="all" style={{ color: '#d05f1ef1', background: '#fff' }}>All time</option>
          </select>
        </div>

        {/* Uncategorized warning */}
        {uncategorizedCount > 0 && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertTriangle size={16} color="#D97706" />
            <div style={{ fontSize: 13, color: '#92400E' }}>
              <strong>{uncategorizedCount} expense{uncategorizedCount !== 1 ? 's' : ''}</strong> are uncategorized and excluded from the deductible estimate.
            </div>
          </div>
        )}

        {/* Income breakdown */}
        {incomeGroups.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #BBF7D0', overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F0FDF4' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUp size={14} color="#15803d" />
                <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Income</div>
              </div>
              {incomeGroups.length > 0 && (
                <button
                  onClick={() => setIncomeExpanded(incomeExpanded.size ? new Set() : new Set(incomeGroups.map(([c]) => c)))}
                  style={{ fontSize: 11, color: T.tealDeep, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 }}
                >
                  {incomeExpanded.size ? 'Collapse all' : 'Expand all'}
                </button>
              )}
            </div>

            {incomeGroups.map(([cat, { items, total }]) => {
              const isOpen = incomeExpanded.has(cat);
              return (
                <div key={cat} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <div
                    onClick={() => toggleIncome(cat)}
                    style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ color: '#9CA3AF', flexShrink: 0, display: 'flex' }}>
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#15803d', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {categoryLabel(cat)}
                      </div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 1 }}>
                        {INCOME_CATEGORY_TAX_MAP[cat] || ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#15803d', ...amountStyle() }}>
                        {formatAmount(total, defaultCurrency)}
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{items.length} item{items.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ background: '#F9FAFB', borderTop: '1px solid #F3F4F6' }}>
                      {[...items].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(e => {
                        const amt = parseFloat(e.convertedTotal ?? e.total) || 0;
                        return (
                          <div key={e.id} style={{ padding: '9px 16px 9px 42px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #F3F4F6' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {e.vendor}
                              </div>
                              <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                                {e.date}
                                {e._sourceFile && ` · ${e._sourceFile}`}
                              </div>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', flexShrink: 0, ...amountStyle() }}>
                              {formatAmount(amt, defaultCurrency)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Expense category breakdown */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Expenses by IRS Category</div>
            {groups.length > 0 && (
              <button
                onClick={() => setExpanded(expanded.size ? new Set() : new Set(groups.map(([c]) => c)))}
                style={{ fontSize: 11, color: T.tealDeep, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 }}
              >
                {expanded.size ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </div>

          {groups.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
              No expenses for {year === 'all' ? 'any period' : year}
            </div>
          )}

          {groups.map(([cat, { items, total }]) => {
            const isOpen = expanded.has(cat);
            const isMeals = cat === MEALS_CAT;
            const isNonDeductible = NON_DEDUCTIBLE.has(cat);
            const isUncategorized = cat === UNCATEGORIZED_CAT;
            const deductibleAmt = isMeals ? total * 0.5 : (isNonDeductible || isUncategorized) ? 0 : total;

            return (
              <div key={cat} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <div
                  onClick={() => toggle(cat)}
                  style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ color: '#9CA3AF', flexShrink: 0, display: 'flex' }}>
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isNonDeductible ? '#9CA3AF' : isUncategorized ? '#D97706' : '#111', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {cat}
                      {isUncategorized && <AlertTriangle size={12} color="#D97706" />}
                      {isMeals && (
                        <span style={{ fontSize: 10, background: '#FEF9C3', color: '#92400E', padding: '1px 5px', borderRadius: 4, fontWeight: 700, flexShrink: 0 }}>50%</span>
                      )}
                    </div>
                    {isMeals && (
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 1 }}>
                        {formatAmount(deductibleAmt, defaultCurrency)} deductible
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: isNonDeductible ? '#9CA3AF' : '#111', ...amountStyle() }}>
                      {formatAmount(total, defaultCurrency)}
                    </div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{items.length} item{items.length !== 1 ? 's' : ''}</div>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ background: '#F9FAFB', borderTop: '1px solid #F3F4F6' }}>
                    {[...items].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(e => {
                      const amt = parseFloat(e.convertedTotal ?? e.total) || 0;
                      const isRefund = isExpenseRefund(e);
                      return (
                        <div key={e.id} style={{ padding: '9px 16px 9px 42px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #F3F4F6' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                              {e.vendor}
                              {isRefund && (
                                <span style={{ fontSize: 10, background: '#DCFCE7', color: '#15803D', borderRadius: 4, padding: '1px 5px', fontWeight: 700, flexShrink: 0 }}>REFUND</span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                              {e.date}
                              {e.category && ` · ${categoryLabel(e.category)}`}
                              {e._sourceFile && ` · ${e._sourceFile}`}
                            </div>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: isRefund ? '#15803D' : '#374151', flexShrink: 0, ...amountStyle() }}>
                            {isRefund && '−'}{formatAmount(amt, defaultCurrency)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Export */}
        <button
          onClick={handleExport}
          disabled={exporting || filtered.length === 0}
          style={{
            width: '100%', padding: '14px', background: T.gradTeal,
            border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
            color: '#fff', cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: filtered.length === 0 ? 0.5 : 1,
          }}
        >
          <Download size={16} />
          {exporting ? 'Exporting…' : 'Export Tax Report (.xlsx)'}
        </button>
      </div>
    </div>
  );
}
